/**
 * 🛒 Página de Administración de Ecommerce
 * 
 * Ruta: /ecommerce (dentro de AppLayout)
 * Acceso: Protegido con Feature Flag
 * Propósito: Administración y configuración de la tienda en línea
 */

import { FeatureProtectedRoute } from '@/components/FeatureProtectedRoute';
import { useState, useEffect } from 'react';
import { useAdminToast } from '@/contexts/AdminToastContext';
import { useNavigate } from 'react-router-dom';
import { getEcommerceUrl, getEcommerceBaseUrl } from '@/lib/ecommerce-urls';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@smarty/shared';
import {
  Badge,
  BlockStack,
  Button,
  Card,
  Icon,
  InlineStack,
  Layout,
  List,
  Page,
  Text,
} from '@/lib/polaris';
import { StoreIcon } from '@/lib/polaris-icons';
import { PageBreadcrumbTitle } from '@/components/polaris/PageBreadcrumbTitle';
import { ROUTES } from '@/config/routes.config';
import { devLog } from '@/lib/utils';

function EcommercePageContent() {
  // `toast` se usaba 6 veces en este archivo pero NUNCA se declaraba ni se importaba: en
  // runtime era `ReferenceError: toast is not defined`. O sea, copiar la URL de la tienda o
  // toparse con un error de configuracion CRASHEABA la pantalla. El gate fantasma lo tapo.
  const toast = useAdminToast();
  const navigate = useNavigate();
  const { tenantId } = useAuth();
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);
  const [tenantSlug, setTenantSlug] = useState<string | null>(null);

  // Esta página solo en panel de mercaderes (web.smarty.lat o localhost). No en marketing ni en admin (super admin).
  useEffect(() => {
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    const h = hostname.split(':')[0];

    if (h === 'smarty.lat' || h === 'www.smarty.lat') {
      navigate('/', { replace: true });
      return;
    }
    if (h === 'admin.smarty.lat' || h === 'admin.smarty.local') {
      navigate('/admin', { replace: true });
      return;
    }
    if (h === 'localhost' || h === '127.0.0.1' || h === 'web.smarty.lat' || h === 'web.smarty.local') {
      return;
    }
    if (h.endsWith('.smarty.lat') || h.endsWith('.smarty.local')) {
      navigate('/', { replace: true });
    }
  }, [navigate]);

  // Cargar slug del tenant
  useEffect(() => {
    const loadTenantSlug = async () => {
      if (!tenantId) return;
      
      try {
        const { data, error } = await supabase
          .from('tenants')
          .select('slug')
          .eq('id', tenantId)
          .single();
        
        if (error) throw error;
        
        if (data?.slug) {
          // VALIDACIÓN: No permitir "app" como slug de tenant (reservado para POS)
          if (data.slug === 'admin' || data.slug === 'app' || data.slug === 'web') {
            devLog.error('❌ Error: El slug está reservado para el panel');
            toast.error('Error en configuración', 'El subdominio está reservado. Contacta al administrador.');
            return;
          }
          
          setTenantSlug(data.slug);
        }
      } catch (error) {
        devLog.error('Error cargando slug del tenant:', error);
      }
    };

    loadTenantSlug();
  }, [tenantId]);

  const handleCopyUrl = (path: string, label: string) => {
    if (!tenantSlug) {
      toast.error('No se pudo determinar el subdominio de la tienda');
      return;
    }
    
    const fullUrl = getEcommerceUrl(path, tenantSlug);
    navigator.clipboard.writeText(fullUrl);
    setCopiedUrl(path);
    toast.success(`URL de ${label} copiada al portapapeles`);
    setTimeout(() => setCopiedUrl(null), 2000);
  };

  const handleOpenUrl = (path: string) => {
    if (!tenantSlug) {
      toast.error('No se pudo determinar el subdominio de la tienda');
      return;
    }
    
    // Validar que tenantSlug NO sea "app" (reservado para POS)
    if (tenantSlug === 'app') {
      toast.error('El subdominio "app" está reservado para el punto de venta');
      return;
    }
    
    const url = getEcommerceUrl(path, tenantSlug);
    if (!url) {
      toast.error('Error al construir la URL de la tienda');
      return;
    }
    
    window.open(url, '_blank');
  };

  const configOptions = [
    {
      title: 'Branding',
      description: 'Personaliza logo, colores y tipografía de tu tienda',
      route: '/ecommerce/branding',
      status: 'available' as const,
    },
    {
      title: 'Métodos de Pago',
      description: 'Configura los métodos de pago disponibles',
      route: '/ecommerce/config/payment-methods',
      status: 'available' as const,
    },
    {
      title: 'Zonas de Delivery',
      description: 'Configura las zonas geográficas de entrega',
      route: '/ecommerce/logistics/delivery-zones',
      status: 'available' as const,
    },
    {
      title: 'Reglas de Envío',
      description: 'Configura descuentos y promociones en delivery',
      route: '/ecommerce/logistics/delivery-rules',
      status: 'available' as const,
    },
    {
      title: 'Métodos de Entrega',
      description: 'Gestiona las opciones de delivery y retiro',
      route: '/ecommerce/logistics/delivery-methods',
      status: 'available' as const,
    },
    {
      title: 'Dominios Personalizados',
      description: 'Configura tu dominio propio para tu tienda',
      route: '/ecommerce/config/domains',
      status: 'available' as const,
    },
  ];

  const links = tenantSlug
    ? [
        {
          label: 'Página de inicio',
          path: '/',
          value: getEcommerceBaseUrl(tenantSlug),
        },
        {
          label: 'Catálogo de productos',
          path: '/pedir',
          value: getEcommerceUrl('/pedir', tenantSlug),
        },
      ]
    : [];

  return (
    <Page
      title={
        <PageBreadcrumbTitle
          icon={StoreIcon}
          title="Tienda online"
          onBack={() => navigate(ROUTES.HOME)}
          backAccessibilityLabel="Volver al inicio"
        />
      }
      primaryAction={{
        content: 'Ver tienda',
        onAction: () => handleOpenUrl('/'),
        disabled: !tenantSlug,
      }}
    >
      <Text as="p" tone="subdued">
        Administra y configura tu tienda en línea
      </Text>
      <Layout>
        <Layout.Section>
          <BlockStack gap="500">
            <Card>
              <BlockStack gap="300">
                <InlineStack align="space-between" blockAlign="center">
                  <Text variant="headingMd" as="h2">
                    Estado del módulo
                  </Text>
                  <Badge tone="success">Activo</Badge>
                </InlineStack>
                <Text as="p" tone="subdued">
                  Tu tienda en línea está activa y lista para recibir pedidos.
                </Text>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="300">
                <Text variant="headingMd" as="h2">
                  Enlaces de tu tienda
                </Text>

                {!tenantSlug ? (
                  <Text as="p" tone="subdued">
                    Cargando información del tenant...
                  </Text>
                ) : (
                  <BlockStack gap="200">
                    {links.map((link) => (
                      <InlineStack key={link.path} align="space-between" blockAlign="center" gap="400">
                        <BlockStack gap="100">
                          <Text as="p" fontWeight="semibold">
                            {link.label}
                          </Text>
                          <Text as="p" tone="subdued">
                            {link.value}
                          </Text>
                        </BlockStack>

                        <InlineStack gap="200">
                          <Button onClick={() => handleOpenUrl(link.path)}>Abrir</Button>
                          <Button
                            variant="secondary"
                            onClick={() => handleCopyUrl(link.path, link.label)}
                          >
                            {copiedUrl === link.path ? 'Copiado' : 'Copiar'}
                          </Button>
                        </InlineStack>
                      </InlineStack>
                    ))}
                  </BlockStack>
                )}
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="300">
                <Text variant="headingMd" as="h2">
                  Configuración
                </Text>
                <Text as="p" tone="subdued">
                  Personaliza tu tienda y ajusta las opciones disponibles.
                </Text>

                <BlockStack gap="200">
                  {configOptions.map((option) => (
                    <InlineStack key={option.route} align="space-between" blockAlign="center" gap="400">
                      <BlockStack gap="100">
                        <InlineStack gap="200" blockAlign="center">
                          <Text as="p" fontWeight="semibold">
                            {option.title}
                          </Text>
                          {option.status === 'available' ? (
                            <Badge tone="success">Disponible</Badge>
                          ) : null}
                        </InlineStack>
                        <Text as="p" tone="subdued">
                          {option.description}
                        </Text>
                      </BlockStack>

                      <Button onClick={() => navigate(option.route)}>Abrir</Button>
                    </InlineStack>
                  ))}
                </BlockStack>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="300">
                <Text variant="headingMd" as="h2">
                  Funcionalidades futuras
                </Text>
                <Text as="p" tone="subdued">
                  Funcionalidades en desarrollo que se agregarán próximamente.
                </Text>
                <List type="bullet">
                  <List.Item>Dominio propio</List.Item>
                  <List.Item>QR en mesa</List.Item>
                  <List.Item>Notificaciones de pedido</List.Item>
                  <List.Item>Integración de pagos</List.Item>
                </List>
              </BlockStack>
            </Card>
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

export default function EcommercePage() {
  return (
    <FeatureProtectedRoute feature="ecommerce">
      <EcommercePageContent />
    </FeatureProtectedRoute>
  );
}
